import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import update from 'immutability-helper';
import { arrayMove } from 'react-sortable-hoc';
import * as contentActions from '../../actions/contentActions';
import * as imageActions from '../../actions/imageActions';
import PageContainer from '../Global/Containers/PageContainer';
import ContentCreationHeader from '../Global/Misc/ContentCreationHeader';
import ManageContentForm from './ManageContentForm';
import MainContentContainer from '../Global/Containers/MainContentContainer';

class ManageContentPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      content: { ...props.initialContent },
      errors: {},
      loading: false,
      imageUploading: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.initialContent.id !== nextProps.initialContent.id) {
      if (nextProps.location.query.progression === `true`) {
        this.addProgression(nextProps);
      } else {
        this.setState({ content: nextProps.initialContent });
      }
    }
  }

  addProgression = (nextProps) => {
    const content = { ...nextProps.initialContent };
    delete content.id;
    this.setState({ content });
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    const content = { ...this.state.content };
    content.main_content = arrayMove(content.main_content, oldIndex, newIndex);
    this.setState({ content });
  }

  addInput = (inputType) => {
    switch (inputType) {
      case `heading`:
      case `text`:
        return update(this.state.content, {
          main_content: { $push: [{ data: ``, static: false, type: inputType }] },
        });

      case `bullet_list`:
      case `number_list`:
        return update(this.state.content, {
          main_content: { $push: [{
            data: [{ text: `` }], static: false, type: inputType,
          }] },
        });

      case `accordion`:
        return update(this.state.content, {
          main_content: { $push: [{
            data: [{ title: ``, detail: `` }], static: false, type: inputType,
          }] },
        });

      case `image`:
        return update(this.state.content, {
          main_content: { $push: [{
            data: { src: ``, alt: ``, id: 0, title: `` }, static: false, type: inputType,
          }] },
        });

      case `bespoke_stream`:
        return update(this.state.content, {
          main_content: { $push: [{
            data: { iframe: ``, src: ``, stream_id: ``, provider: `` }, static: false, type: inputType,
          }] },
        });
      // no default
    }
    return this.state.content;
  }

  handleAddInputClick = (e, inputType) => {
    e.preventDefault();
    this.setState({ content: this.addInput(inputType) });
  }

  addTextarea = (contentIndex) => {
    const newContent = update(this.state.content, {
      main_content: { $splice: [[contentIndex + 1, 0, { data: ``, static: false, type: `text` }]] },
    });
    this.setState({ content: newContent });
  }

  addListItem = (contentIndex, nestedIndex) => {
    let newContent;
    if (nestedIndex !== undefined) {
      newContent = update(this.state.content, {
        main_content: { [contentIndex]: {
          data: { $splice: [[nestedIndex + 1, 0, { text: `` }]] },
        } },
      });
    } else {
      newContent = update(this.state.content, {
        main_content: { [contentIndex]: { data: {
          $push: [{ text: `` }],
        } } },
      });
    }
    this.setState({ content: newContent });
  }

  addAccordionRow = (contentIndex, nestedIndex) => {
    let newContent;
    if (nestedIndex !== undefined) {
      newContent = update(this.state.content, {
        main_content: { [contentIndex]: {
          data: { $splice: [[nestedIndex + 1, 0, { title: ``, detail: `` }]] },
        } },
      });
    } else {
      newContent = update(this.state.content, {
        main_content: { [contentIndex]: { data: {
          $push: [{ title: ``, detail: `` }],
        } } },
      });
    }
    this.setState({ content: newContent });
  }

  updateCheckbox = (field, value) => {
    const newContent = { ...this.state.content };
    // If it is a simple array that needs to be passed up (no objects or keys, just a list of numbers)
    if (field === `age`) {
      if (newContent[field].filter(object => object === value).length > 0) {
        // Remove from the list
        const filteredArray = newContent[field].filter(option => option !== value);
        newContent[field] = filteredArray;
      } else {
        // Add to the list
        newContent[field] = newContent[field].concat(value);
      }
    } else if (newContent[field].filter(object => object.id === value).length > 0) {
      // Remove from the list
      const filteredArray = newContent[field].filter(option => option.id !== value);
      newContent[field] = filteredArray;
    } else {
      // Add to the list
      newContent[field] = newContent[field].concat({ id: value });
    }
    this.setState({ content: newContent });
  }

  updateRadio = (field, value) => {
    const newContent = { ...this.state.content };
    // If a value already exists then replace it with the new value
    if (newContent[field].length) {
      if (newContent[field].filter(object => object.id !== value).length > 0) {
        newContent[field] = [{ id: value }];
      }
    // If not then simply add it as a single object in an array
    } else {
      newContent[field] = [{ id: value }];
    }
    this.setState({ content: newContent });
  }

  updateImage = (contentIndex, image) => {
    this.setState({ imageUploading: true }, () => {
      this.props.actions.uploadImage(image)
      .then((response) => {
        if (response.ok) {
          response.json().then((res) => {
            this.addImageToContent(res.data, contentIndex);
          });
        }
      })
      .catch(() => {
        this.setState({ imageUploading: false });
      });
    });
  }

  addImageToContent = (image, contentIndex) => {
    const { src, alt, id, title, type, content_id } = image;
    // Have to check that it is a number rather than simply if (contentIndex) as the contentIndex can be '0' which would return false
    if (typeof contentIndex === `number`) {
      const newContent = update(this.state.content, {
        main_content: { [contentIndex]: { data: {
          src: { $set: src }, alt: { $set: alt }, id: { $set: id }, title: { $set: title }, type: { $set: type }, content_id: { $set: content_id },
        } } },
      });
      this.setState({ content: newContent });
    } else {
      const newContent = update(this.state.content, {
        image: { data: {
          src: { $set: src }, alt: { $set: alt }, id: { $set: id }, title: { $set: title }, type: { $set: type }, content_id: { $set: content_id },
        } },
      });
      this.setState({ content: newContent });
    }
    this.setState({ imageUploading: false });
  }

  deleteImage = (contentIndex) => {
    // Have to check that it is a number rather than simply if (contentIndex) as the contentIndex can be '0' which would return false
    if (typeof contentIndex === `number`) {
      const newContent = update(this.state.content, {
        main_content: { [contentIndex]: { data: {
          src: { $set: `` }, alt: { $set: `` }, id: { $set: 0 }, title: { $set: `` }, type: { $set: `` }, content_id: { $set: 0 },
        } } },
      });
      this.setState({ content: newContent });
    } else {
      const newContent = update(this.state.content, {
        image: { data: {
          src: { $set: `` }, alt: { $set: `` }, id: { $set: 0 }, title: { $set: `` }, type: { $set: `` }, content_id: { $set: 0 },
        } },
      });
      this.setState({ content: newContent });
    }
  }

  updateMainContent = (field, event, contentIndex, mainContentPatch) => {
    const value = event.target.value;
    switch (mainContentPatch.type) {
      case `heading`:
      case `text`: {
        const newContent = update(this.state.content, {
          main_content: { [contentIndex]: { data: { $set: value } } },
        });
        this.setState({ content: newContent });
        break;
      }

      case `bullet_list`:
      case `number_list`: {
        const newContent = update(this.state.content, {
          main_content: { [contentIndex]: { data: { [mainContentPatch.index]: { text: { $set: value } } } } },
        });
        this.setState({ content: newContent });
        break;
      }

      case `accordion`: {
        const newContent = update(this.state.content, {
          main_content: { [contentIndex]: { data: { [mainContentPatch.index]: { [mainContentPatch.fieldName]: { $set: value } } } } },
        });
        this.setState({ content: newContent });
        break;
      }

      case `image`:
        if (value === ``) {
          this.deleteImage(contentIndex);
        } else {
          this.updateImage(contentIndex, event.target.files[0]);
        }
        break;

      case `bespoke_stream`: {
        const newContent = update(this.state.content, {
          main_content: { [contentIndex]: { data: { iframe: { $set: value } } } },
        });
        this.setState({ content: newContent });
      }
      // no default
    }
  }

  updateContentState = (e, contentIndex, mainContentPatch) => {
    const field = e.target.name;

    if (field.includes(`main_content`)) {
      this.updateMainContent(field, e, contentIndex, mainContentPatch);
    } else {
      let fieldValue;
      switch (e.target.type) {
        case `checkbox`:
        case `radio`:
          // All meta is passed back in an array (even if radio buttons so we need alter the data here)
          fieldValue = Number(e.target.value);
          if (e.target.type === `checkbox`) {
            this.updateCheckbox(field, fieldValue);
          } else if (e.target.type === `radio`) {
            this.updateRadio(field, fieldValue);
          }
          break;

        // This is for the hero image only, not image in main_content
        case `file`:
          // If the value is an empty string we are trying to delete the image
          if (e.target.value === ``) {
            this.deleteImage();
          } else {
            this.updateImage(contentIndex, e.target.files[0]);
          }
          break;

        default: {
          // If it's a simple text/textarea component we simply need to update the value
          const newContent = update(this.state.content, {
            [field]: { $set: e.target.value },
          });
          this.setState({ content: newContent });
        }
      }
    }
  }

  handleDiagramChange = (diagram, contentIndex) => {
    if (diagram === null) {
      this.deleteImage(contentIndex);
    } else {
      this.addImageToContent(diagram, contentIndex);
    }
  }

  deleteInput = (contentIndex, nestedIndex) => {
    if (nestedIndex !== undefined) {
      const newContent = update(this.state.content, {
        main_content: { [contentIndex]: {
          data: { $splice: [[nestedIndex, 1]] },
        } },
      });
      this.setState({ content: newContent });
    } else {
      const newContent = update(this.state.content, {
        main_content: { $splice: [[contentIndex, 1]] },
      });
      this.setState({ content: newContent });
    }
  }

  saveContent = (e) => {
    e.preventDefault();
    this.setState({ loading: true });
    this.props.actions.saveContent(this.state.content)
      .then(() => {
        this.setState({ loading: false });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  getContentTypeFromURL = () => {
    let type;
    if (this.props.location.pathname.includes(`document`)) {
      type = `document`;
    } else {
      type = `drill`;
    }
    return type;
  }

  render() {
    const { metadataOptions } = this.props;
    const { content, loading, errors, imageUploading } = this.state;

    let saveButtonDisabled = false;
    if (`title` in content && content.title.length === 0) {
      saveButtonDisabled = true;
    } else if (imageUploading === true) {
      saveButtonDisabled = true;
    }

    return (
      <form className="drill-creation-form">
        <ContentCreationHeader
          contentTitle={content.title}
          onTitleChange={this.updateContentState}
          onSaveClick={this.saveContent}
          loading={loading}
          disableSubmit={saveButtonDisabled}
        />
        <PageContainer classes="bg-white">
          <MainContentContainer>
            <ManageContentForm
              metadataOptions={metadataOptions}
              onChange={this.updateContentState}
              onDiagramChange={this.handleDiagramChange}
              content={content}
              errors={errors}
              addInput={this.handleAddInputClick}
              addTextarea={this.addTextarea}
              addAccordionRow={this.addAccordionRow}
              addListItem={this.addListItem}
              deleteInput={this.deleteInput}
              onSortEnd={this.onSortEnd}
              getContentPage={this.getContentTypeFromURL}
            />
          </MainContentContainer>
        </PageContainer>
      </form>
    );
  }
}

ManageContentPage.propTypes = {
  initialContent: PropTypes.object.isRequired,
  metadataOptions: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

function mapStateToProps(state, ownProps) {
  let content;
  const contentId = ownProps.params.id; // Get the content ID from the URL
  let type;
  let mainContentData;

  if (ownProps.location.pathname.includes(`document`)) {
    type = `document`;
    mainContentData = ``;
  } else {
    type = `drill`;
    mainContentData = `Key Coaching Points`;
  }

  // If the URL contains a content ID then we set content to be response from the queryapi which is fired in routes.js
  if (contentId) {
    content = state.content;
  } else {
    content = {
      image: {
        data: {
          src: ``,
        },
      },
      age: [],
      phase_of_the_game: [],
      theme: [],
      type_of_practice: [],
      stage_of_practice: [],
      unit_focus: [],
      principles_of_play: [],
      area_of_pitch: [],
      area_size: ``,
      time: 0,
      number_of_players: 0,
      bibs: 0,
      balls: 0,
      cones: 0,
      type,
      title: ``,
      summary: ``,
      main_content: [{
        type: `heading`,
        data: mainContentData,
        static: false,
      },
      {
        type: `accordion`,
        data: [{
          detail: ``,
          title: ``,
        }],
        static: false,
      }],
    };
  }

  const metadataFormattedForInput = {};
  Object.keys(state.metadata).forEach((key) => {
    metadataFormattedForInput[key] = state.metadata[key].map(option =>
      ({ value: option.id, label: option.name }),
    );
  });

  return {
    initialContent: content,
    metadataOptions: metadataFormattedForInput,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign({}, contentActions, imageActions), dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ManageContentPage);



// WEBPACK FOOTER //
// ./library/app/components/Create/ManageContentPage.js